import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AppConfigContext = createContext(null);

const DEFAULTS = {
  maintenance_mode: false,
  registration_open: true,
  read_only_mode: false,
  announcement_active: false,
  announcement_text: '',
  announcement_color: 'cyan',
  announcement_link: '',
  announcement_dismissible: true,
  popup_active: false,
  popup_title: '',
  popup_body: '',
  theme_mode: 'default',
  theme_start: null,
  theme_end: null,
  offer_active: true,
  offer_label_premium: '🔥 Offer · 30 days left',
  offer_label_promo: '⚡ Offer · 30 days left',
  premium_price_inr: 39,
  promo_price_inr: 1,
  premium_original_price_inr: 99,
  promo_original_price_inr: 9,
  post_max_chars: 250,
  show_usd: true,
  app_version: '1.0.0',
  notif_votes_enabled: true,
  notif_comments_enabled: true,
};

const COLOR_MAP = {
  cyan:   'bg-cyan-600 text-white',
  blue:   'bg-blue-600 text-white',
  green:  'bg-green-600 text-white',
  red:    'bg-red-600 text-white',
  orange: 'bg-orange-500 text-white',
  purple: 'bg-purple-600 text-white',
  yellow: 'bg-yellow-400 text-gray-900',
};

const DISMISSED_KEY = 'litx_banner_dismissed_text';
const POPUP_SEEN_KEY = 'litx_popup_seen_title';

// Themes that force light mode (non-default themes override dark mode)
const THEMED_MODES = ['christmas', 'diwali', 'newyear', 'valentine'];
// BW is special — keeps current mode but applies grayscale

export const AppConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [popupSeen, setPopupSeen] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/config/public`);
      const data = res.data || {};
      const merged = { ...DEFAULTS, ...data };

      // Auto-expire scheduled theme past its end date
      const now = new Date();
      if (merged.theme_end && new Date(merged.theme_end) < now) {
        merged.theme_mode = 'default';
      }

      setConfig(prev => {
        // Announcement text changed → force banner to show again
        if (prev.announcement_text !== merged.announcement_text) {
          setBannerDismissed(false);
        } else {
          const dismissed = sessionStorage.getItem(DISMISSED_KEY);
          if (dismissed === merged.announcement_text) setBannerDismissed(true);
        }
        // Popup title changed → show again
        if (prev.popup_title !== merged.popup_title) {
          setPopupSeen(false);
        } else {
          const seen = sessionStorage.getItem(POPUP_SEEN_KEY);
          if (seen === merged.popup_title) setPopupSeen(true);
        }
        return merged;
      });
    } catch {
      console.log('AppConfig: using defaults');
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    const interval = setInterval(fetchConfig, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchConfig]);

  // ── Theme management ─────────────────────────────────────────────────────
  // When a seasonal theme is active:
  // 1. Add theme-* class to <html>
  // 2. REMOVE the .dark class so dark: Tailwind classes become inactive
  //    (seasonal themes are always light-mode-based, except bw which is neutral)
  // 3. When theme is removed, restore user's preferred dark mode setting
  useEffect(() => {
    const html = document.documentElement;
    const allThemeClasses = ['theme-christmas', 'theme-bw', 'theme-diwali', 'theme-newyear', 'theme-valentine'];

    // Remove all theme classes first
    html.classList.remove(...allThemeClasses);

    if (config.theme_mode && config.theme_mode !== 'default') {
      // Add the new theme class
      html.classList.add(`theme-${config.theme_mode}`);

      // For colour themes (not BW), force light mode by removing .dark
      if (THEMED_MODES.includes(config.theme_mode)) {
        // Save current dark preference before removing it
        if (html.classList.contains('dark')) {
          sessionStorage.setItem('litx_dark_before_theme', 'true');
        }
        html.classList.remove('dark');
      }
    } else {
      // Theme reset to default — restore dark mode if user had it
      const hadDark = sessionStorage.getItem('litx_dark_before_theme');
      const savedTheme = localStorage.getItem('theme');
      if (hadDark === 'true' || savedTheme === 'dark') {
        html.classList.add('dark');
        sessionStorage.removeItem('litx_dark_before_theme');
      }
    }
  }, [config.theme_mode]);

  // ── Banner has-banner class ────────────────────────────────────────────
  useEffect(() => {
    const active = config.announcement_active && !!config.announcement_text && !bannerDismissed;
    document.documentElement.classList.toggle('has-banner', active);
  }, [config.announcement_active, config.announcement_text, bannerDismissed]);

  const dismissBanner = () => {
    setBannerDismissed(true);
    sessionStorage.setItem(DISMISSED_KEY, config.announcement_text);
  };

  const dismissPopup = () => {
    setPopupSeen(true);
    sessionStorage.setItem(POPUP_SEEN_KEY, config.popup_title);
  };

  const showBanner = config.announcement_active && !!config.announcement_text && !bannerDismissed;
  const showPopup  = config.popup_active && !!config.popup_title && !popupSeen;
  const bannerColorClass = COLOR_MAP[config.announcement_color] || COLOR_MAP.cyan;

  // Whether a seasonal theme is active (used by Profile to hide the dark toggle)
  const isSeasonalTheme = config.theme_mode && config.theme_mode !== 'default';

  return (
    <AppConfigContext.Provider value={{
      config,
      loaded,
      showBanner,
      showPopup,
      bannerColorClass,
      dismissBanner,
      dismissPopup,
      refreshConfig: fetchConfig,
      isSeasonalTheme,
    }}>
      {children}
    </AppConfigContext.Provider>
  );
};

export const useAppConfig = () => useContext(AppConfigContext);